import { useEffect, useState, useCallback } from 'react';
import {
  listIncomingTrainerLinkRequests,
  acceptTrainerLinkRequest,
  rejectTrainerLinkRequest,
  resolveProfileById,
  type TrainerClientLink,
} from '@/lib/trainers';

export type CoachInvite = TrainerClientLink & {
  coachName: string;
  coachHandle: string | null;
};

export function useCoachInvites() {
  const [pendingInvites, setPendingInvites] = useState<CoachInvite[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const links = await listIncomingTrainerLinkRequests();
      const enriched: CoachInvite[] = await Promise.all(
        links.map(async (link) => {
          const profile = await resolveProfileById(link.coach_user_id);
          return {
            ...link,
            coachName: profile?.display_name || profile?.handle || 'Unknown Coach',
            coachHandle: profile?.handle ?? null,
          };
        }),
      );
      setPendingInvites(enriched);
    } catch (err) {
      console.warn('[useCoachInvites] error:', err);
      setPendingInvites([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const acceptInvite = useCallback(async (linkId: string) => {
    const result = await acceptTrainerLinkRequest(linkId);
    if (result.ok) {
      setPendingInvites((prev) => prev.filter((i) => i.id !== linkId));
    }
    return result;
  }, []);

  const declineInvite = useCallback(async (linkId: string) => {
    const result = await rejectTrainerLinkRequest(linkId);
    if (result.ok) {
      setPendingInvites((prev) => prev.filter((i) => i.id !== linkId));
    }
    return result;
  }, []);

  return { pendingInvites, loading, acceptInvite, declineInvite, refresh };
}
